namespace TR {
    type HotelService = Core.Hotels.HotelService;
    type FieldGroupService = Core.FieldGroupService;
    type TripService = Core.TripService;
    class DealsFindParameters {
        url: string;
        sort: number;
        fieldFlags: any;
        paging: any;
        includeCruises: boolean;
        destination: any;
        discountedPricesOnly: boolean;
        flexibility: number;
    }

    export class HolidayDeals {

        API: string = '/api/holidays/deals';
        DURATIONS: number[] = TRPCONFIG.Domain.HolidayDealsDurations.map(d => parseInt(d, 10));
        runningRequest: any;

        constructor(
            private $http: ng.IHttpService,
            private $location: ng.ILocationService,
            private $filter: any,
            private $q: ng.IQService,
            private dateUtil: any,
            private resourceService: Core.ResourceService,
            private getPropertyTypeFromResKey: any,
            private imageService: any,
            private occupancyService: Core.OccupancyService,
            private fieldGroupService: FieldGroupService,
            private filtering: any,
            private pageStrand: any,
            private tripService: TripService,
            private hotelService: HotelService,
            private analytics: any,
            private flightSearch: any,
            private geo: any,
            private preferences: any
        ) {
            this.runningRequest = this.$q.defer();
        }

        find({
            page = 0, sort = 1, pageSize = TRPCONFIG.Domain.HolidayDealsPageSize, destination, saveToUrl,
            includeAggregates = true, discountedPricesOnly = false,
            flexibility,
            includeCruises = TRPCONFIG.Domain.HolidayDealsCruisesEnabled
        }) {
            let findParams = new DealsFindParameters();
            findParams.fieldFlags = this.fieldGroupService.getFieldGroup(
                Core.FieldType.Global,
                Core.FieldType.Titles,
                Core.FieldType.Url,
                Core.FieldType.Description,
                Core.FieldType.Summary,
                Core.FieldType.HolidayDeals,
                Core.FieldType.LegacyFacilities,
                Core.FieldType.Weather,
                Core.FieldType.Distances,
                Core.FieldType.Recommendations,
                Core.FieldType.Airports,
                Core.FieldType.TransferTimes
            );
            findParams.paging = { Size: pageSize, Index: page };
            findParams.sort = sort;
            findParams.url = `${this.API}/search`;
            findParams.includeCruises = includeCruises;
            findParams.destination = destination;
            findParams.discountedPricesOnly = discountedPricesOnly;
            findParams.flexibility = flexibility;

            return this.$q.all({
                params: this.getFullParameters(saveToUrl),
                filters: this.filtering.getFilters(this.getDefaultFilters(), this.pageStrand.get('Holiday'))
            })
            .then(res => this.$q.all({
                deals: this._findDeals(findParams, res.filters, includeAggregates, res.params),
                summary: this.getSearchSummary(res.params),
            }))
            .then((res: any) => ({
                Products: this._enrichProducts(res.deals.data.Hotels, res.deals.req),
                Aggregates: res.deals.data.Aggregates,
                Paging: {
                    Size: res.deals.req.Paging.Size,
                    Index: res.deals.req.Paging.Index,
                    Total: res.deals.data.Aggregates
                        ? res.deals.data.Aggregates.FilteredHotelCount
                        : 0,
                    UnfilteredTotal: res.deals.data.Aggregates
                        ? res.deals.data.Aggregates.HotelCount
                        : 0
                },
                Info: {
                    duration: res.deals.req.Duration,
                    searchSummary: res.summary,
                    filters: res.deals.filters,
                    airportsTitles: res.deals.data.AirportTitles
                }
            }));
        }

        getMapCoordinates() {
            let findParams = new DealsFindParameters();
            const fields = 'Hotels.Id,Hotels.Latitude,Hotels.Longitude,Hotels.DealMinPrice';

            findParams.fieldFlags = this.fieldGroupService.getFieldGroup(
                Core.FieldType.Global
            );
            findParams.paging = null;
            findParams.sort = 0;
            findParams.url = `${this.API}/search?fields=${fields}`;

            return this.filtering
                       .getFilters(this.getDefaultFilters(), this.pageStrand.get('Holiday'))
                       .then(filters => this._findDeals(findParams, filters, false))
                       .then(res => res.data.Hotels.filter(h => (h.Latitude || h.Longitude)));
        }

        getDealsForHotel(
            estabId: number,
            durations: number[] | undefined,
            dates: Date[]
        ) {
            const params:any = this.deserialiseParameters();
            const fieldFlags = this.fieldGroupService.getFieldGroup(
                Core.FieldType.HolidayDeals,
                Core.FieldType.Airports,
                Core.FieldType.TransferTimes
            );

            let datesAndDurations = _(dates || [params.startDate])
                .map(date => this.dateUtil.serialiseDate(date))
                .flatMap(date => {
                    return _.map(durations || [params.duration], duration => ({ Date: date, Duration: duration }));
                })
                .value();

            let request: any = {
                Id: estabId,
                DatesAndDurations: datesAndDurations,
                DomainId: TRPCONFIG.Domain.DomainId,
                CultureCode: TRPCONFIG.Domain.CultureCode,
                CurrencyCode: TRPCONFIG.Domain.CurrencyCode,
                Adults: params.occupancy[0].Adults,
                ChildAges: params.occupancy[0].ChildAges,
                OriginAirports: params.depAir,
                FieldFlags: fieldFlags,
                BoardTypes: []
            };

            return this.$http.post(`${this.API}/getdealsforhotel`, request)
                       .then(res => res.data);
        }

        getDealsFromMap(estabId) {
            const { startDate } = this.deserialiseParameters();
            const dates = this.dateUtil.getDateInterval(startDate, 7);
            return this.getDealsForHotel(estabId, undefined, dates);
        }

        getAggregates(saveToUrl = true, presetParams = {}) {
            let findParams = new DealsFindParameters();

            findParams.fieldFlags = this.fieldGroupService.getFieldGroup(
                Core.FieldType.HolidayDeals
            );
            findParams.paging = { Size: 0, Index: 0 };
            findParams.sort = 5;
            findParams.url = `${this.API}/search`;
            findParams.includeCruises = false;

            return this.$q.all([
                this.getFullParameters(saveToUrl, presetParams),
                this.filtering.getFilters(this.getDefaultFilters(), this.pageStrand.get('Holiday'))
            ])
            .then(res => ({ params: res[0], filters: _.omit(res[1], 'ChildDestinations') }))
            .then((res:any) => this._findDeals(findParams, res.filters, true, res.params))
            .then(res => res && res.data);
        }

        enrichDeals(deals, boardTypeFilters) {
            return deals.map(d => {
                d.HotelDeal.CheckInDate =
                    this.dateUtil.parseDate(d.HotelDeal.CheckInDate);
                d.HotelDeal.CheckOutDate =
                    this.dateUtil.parseDate(d.HotelDeal.CheckOutDate);

                d.DepartureEpoch = angular
                    .copy(d.HotelDeal.CheckInDate)
                    .setHours(0, 0, 0, 0);

                d.Duration = this.dateUtil.getDifferenceInDays(
                    d.HotelDeal.CheckInDate,
                    d.HotelDeal.CheckOutDate
                );

                let bt;
                if (boardTypeFilters && boardTypeFilters.length) {
                    bt = _.chain(d.HotelDeal.BoardTypeMinPrices)
                        .filter(v => boardTypeFilters.indexOf(v.BoardType) > -1)
                        .sortBy(v => v.MinPrice)
                        .value()[0];
                }

                const hotelPrice = bt ? bt.MinPrice : d.HotelDeal.Price;
                d.Price = d.FlightsDeal.Price + hotelPrice;
                const num = this.occupancyService.getNumberOfNonInfantPassengers();
                d.PricePerPerson = d.Price / num;

                return d;
            });
        }

        selectDeal(deal: any, estabId: number, original: any, originalTimes: any) {
            const params = this.deserialiseParameters();
            return this.hotelService.getHotelDetails({
                AvailabilitySearchId: deal.HotelDeal.AvailabilitySearchId,
                EstabId: estabId,
                RoomAndBoardIds: [deal.selectedBoardType.Id],
                DiscountCode: TRPCONFIG.DiscountCode,
                BookingPath: 4
            })
            .then(details => {
                deal.EstabId = estabId;
                deal.HotelDeal.ProductId = details.DetailsId;
                return deal;
            })
            .then(deal => this.tripService.getTripIdForHolidayDeal(deal, params.occupancy))
            .then(tripId => {
                this._trackDealChanges(original, deal, originalTimes);
                return this.$location.url(`holidays/options/${tripId}/`);
            });
        }

        serialiseParameters(params: any, toUrl: boolean) {
            const getParams = {
                depAir: params.airportsCodes ? params.airportsCodes.map(a => a.Code).join('-') : undefined,
                startDate: params.startDate ? this.dateUtil.format(params.startDate, 'yyyy-MM-dd') : undefined,
                duration: params.duration,
                occ: params.occupancy ? this.occupancyService.serialise(params.occupancy) : undefined,
                flex: params.flexibility
            };
            return toUrl ? jQuery.param(getParams) : getParams;
        }

        deserialiseParameters() {
            const params = this.$location.search();
            return {
                depAir: params.depAir ? params.depAir.split('-') : undefined,
                startDate: params.startDate
                    ? this.dateUtil.parseDate(params.startDate)
                    : undefined,
                duration: parseInt(params.duration, 10),
                occupancy: params.occ
                    ? this.occupancyService.parseOccupancyString(params.occ)
                    : undefined,
                flexibility: parseInt(params.flex || '3', 10)
            };
        }

        getFullParameters(saveToUrl = true, presetParams = {}) {
            const params = _.merge(this.deserialiseParameters(), presetParams);
            const queryString:any = this.serialiseParameters(presetParams, false);
            if (params.startDate && params.depAir && params.occupancy && params.duration) {
                return this.$q.when(params);
            }

            if (!params.startDate) {
                params.startDate =
                    this.dateUtil.getNextSaturday(TRPCONFIG.Domain.HolidayDealsDefaultWeeks);
                queryString.startDate = this.dateUtil.format(params.startDate, 'yyyy-MM-dd');
            }

            if (!params.occupancy) {
                params.occupancy = this.occupancyService.parseOccupancyString(
                    'A2'
                );
                queryString.occ = TRPCONFIG.Domain.HolidayDealsDefaultOccupancy;
            }

            if (!params.duration) {
                params.duration = TRPCONFIG.Domain.HolidayDealsDefaultDuration;
                queryString.duration = TRPCONFIG.Domain.HolidayDealsDefaultDuration;
            }

            const promise = !params.depAir || !params.depAir.length
                ? this.preferences
                    .get('holidayaSsearch')
                    .then((holiday:any) => holiday && holiday.airports
                        ? holiday.airports
                        : this.geo
                            .getNearestAirports()
                            .then(airports => airports.map(a => a.Code))
                    )
                    .then(airports => {
                        params.depAir = airports && airports.length > 0
                            ? airports
                            : TRPCONFIG.Domain.HolidayDealsDefaultAirports;
                        queryString.depAir =  params.depAir.join('-');
                        return params;
                    })
                : this.$q.when(params);

            return promise.then(params => {
                if (saveToUrl) {
                    let search = this.$location.search();
                    _.merge(search, queryString, { dealsOnly: 1 });
                    this.$location.search(search).replace();
                }
                return params;
            });
        }

        getDefaultFilters(): any {
            return {
                Stars: [],
                BoardTypes: [],
                ChildDestinations: [],
                FacilityCodes: [],
                PropertyTypes: [],
                RatingRange: null,
                PriceRange: null
            };
        }

        getCurrentFilters() {
            return this.filtering
                .getFilters(this.getDefaultFilters(), this.pageStrand.get('Holiday'));
        }

        getSearchSummary(params) {
            params.occupancy = _.first(params.occupancy);
            const appDate = this.$filter('date')(params.startDate, 'd MMM yyyy');
            const { nights, adults, children } = this._getSummaryElements(params);

            let summary = '';
            summary += `${this.resourceService.get('HOLIDAYDEALSHEADER')} `;
            summary += `${params.duration} ${nights}`;
            summary += `, ${appDate}, `;
            summary += params.occupancy.Adults > 0
                ? `${params.occupancy.Adults} ${adults} `
                : '';
            summary += params.occupancy.ChildAges.length > 0
                ? `${params.occupancy.ChildAges.length} ${children} `
                : '';

            return this.flightSearch
                .getAirportTitles(params.depAir)
                .then(res => {
                    const different = this.resourceService
                        .get('DIFFERENTAIRPORTS')
                        .replace('{0}', res.data.length);

                    const fromString = this.resourceService.get('FROM');

                    return res.data.length === 1
                        ? `${summary} ${fromString} ${res.data}`
                        : `${summary} ${different}`;
                });
        }

        enrichHotel(hotel, request) {
            const imageW = TRPCONFIG.DeviceSettings.HotelAvailabilityImageSize;
            const imageH = imageW;
            const propertyType = this.getPropertyTypeFromResKey(hotel.AcomTypeId, hotel.IsVilla);
            const numberOfNonInfantPassengers = this.occupancyService.getNumberOfNonInfantPassengers();
            hotel.PropertyTypeFrom = this.resourceService.get(propertyType);
            hotel.ImageUrl = this.imageService.getMainImageUrl(hotel.Id, imageW, imageH);
            hotel.CroppedImage = this.imageService.getMainImageUrl(hotel.Id, 270, 150, true);
            hotel.MinPricePerson = hotel.DealMinPrice / numberOfNonInfantPassengers;
            if (hotel.HolidayDeals) {
                hotel.DealWasPrice = hotel.HolidayDeals[0].HotelDeal.WasPrice + hotel.HolidayDeals[0].FlightsDeal.Price;
                hotel.WasPricePerPerson = hotel.DealWasPrice / numberOfNonInfantPassengers;
                hotel.BoardType = this.resourceService.get(
                    this.filtering.getBoardTypeName(hotel.HolidayDeals[0].HotelDeal.BoardType)
                );
            }
            hotel.Duration = request.Duration;
            hotel.OccupancyString = this.occupancyService.getOccupancyString(
                [<Core.Occupancy>_.pick(request, 'Adults', 'ChildAges')]
            );
            return hotel;
        }

        private getMinHolidayPrice() {
            return this._getTeaserPriceRequest()
                .then(r => this.$http.post(`${this.API}/getminholidayprice`, r, { ignoreErrors: true }))
                .then(response => response && response.data);
        }

        private getMinCruisePrice() {
            return this._getTeaserPriceRequest()
                .then(r => this.$http.post(`${this.API}/getmincruiseprice`, r, { ignoreErrors: true }))
                .then(response => response && response.data);
        }

        _getTeaserPriceRequest() {
            return this.getFullParameters(false).then(params => ({
                DatesAndDurations: this._getDatesAndDurations(this.dateUtil.getNextSaturday(2), params.duration, 3),
                Adults: 2,
                DomainId: TRPCONFIG.Domain.DomainId,
                CultureCode: TRPCONFIG.Domain.CultureCode,
                CurrencyCode: TRPCONFIG.Domain.CurrencyCode,
                OriginAirports: params.depAir
            }));
        }

        private _trackDealChanges(orig, sel, originalTimes) {
            if (!orig || !orig.HotelDeal || !orig.FlightsDeal ||
                !sel || !sel.HotelDeal || !sel.FlightsDeal) {
                return;
            }

            const dow = this.$filter('date')(sel.HotelDeal.CheckInDate, 'EEE');

            const priceVariation =
                (sel.HotelDeal.Price + sel.FlightsDeal.Price) -
                (orig.HotelDeal.Price + orig.FlightsDeal.Price);

            this.analytics.track(
                'mixpanel',
                'HolidayDealsSelected',
                {
                    DurationChanged: originalTimes.Duration !== sel.Duration,
                    DateChanged: originalTimes.DepartureEpoch !== sel.DepartureEpoch,
                    FlightChanged: orig.FlightsDeal.FlightId !== sel.FlightsDeal.FlightId,
                    RoomChanged: orig.selectedRoom.MasterId !== sel.selectedRoom.MasterId,
                    BoardTypeChanged: orig.selectedBoardType.BoardType !== sel.selectedBoardType.BoardType,
                    PriceVariation: priceVariation,
                    DayOfWeek: dow
                }
            );
        }

        private _getSummaryElements(params) {
            const nights = this.resourceService
                .get(params.duration === 1 ? 'NIGHT' : 'NIGHTS').toLowerCase();

            const adults = this.resourceService
                .get(params.occupancy.Adults === 1 ? 'ADULT' : 'ADULTS');
            const children = this.resourceService
                .get(params.occupancy.ChildAges.length === 1 ? 'CHILDREN' : 'CHILDREN');

            return { nights, adults, children };
        }

        private _findDeals(
            findParams: DealsFindParameters,
            filters: any,
            includeAggregates,
            params: any = undefined
        ) {

            if (this.runningRequest && includeAggregates) {
                this.runningRequest.resolve('CANCELLED');
                this.runningRequest = this.$q.defer();
            }

            if (!params) {
                params = this.deserialiseParameters();
            }

            let flexibility = angular.isUndefined(findParams.flexibility)
                ? params.flexibility : findParams.flexibility;

            const request = {
                DatesAndDurations: this._getDatesAndDurations(params.startDate, params.duration, flexibility),
                Adults: params.occupancy[0].Adults,
                ChildAges: params.occupancy[0].ChildAges,
                DomainId: TRPCONFIG.Domain.DomainId,
                CultureCode: TRPCONFIG.Domain.CultureCode,
                CurrencyCode: TRPCONFIG.Domain.CurrencyCode,
                OriginAirports: params.depAir,
                FieldFlags: findParams.fieldFlags,
                FilterCriteria: filters,
                SortCriterion: findParams.sort,
                Paging: findParams.paging,
                IncludeAggregates: includeAggregates,
                IncludeCruises: findParams.includeCruises,
                DiscountedPricesOnly: findParams.discountedPricesOnly,
                Destination: findParams.destination
            };

            return this.$http.post(
                findParams.url,
                request,
                { timeout: this.runningRequest.promise })
                .then((res: any) => {
                    res.req = request;
                    res.params = params;
                    res.filters = filters;
                    return res;
                });
        }

        private _enrichProducts(products: any, request: any) {
            return products
                .filter(p => p.IsCruise || p.HolidayDeals)
                .map(p => p.IsCruise ? this._enrichCruise(p) : this.enrichHotel(p, request));
        }

        private _enrichCruise(cruise) {
            cruise.PricePerPerson =
                cruise.DealMinPrice / this.occupancyService.getNumberOfNonInfantPassengers();
            cruise.BoardType = this.resourceService.get(
                this.filtering.getBoardTypeName(cruise.CheapestBoardType)
            );
            return cruise;
        }

        private _getDatesAndDurations(date: Date, duration: number, flexibility: number)
        {
            const cutoffDate = this.dateUtil.todayPlus(2);
            return _(_.range(-(flexibility), flexibility + 1))
                .map(x => this.dateUtil.addDays(date, x))
                .filter(date => date > cutoffDate)
                .map(date => ({
                    Date: this.dateUtil.serialiseDate(date),
                    Duration: duration
                }))
                .value();
        }
    }

    angular
        .module('TR')
        .service('holidayDeals', HolidayDeals);
}
